import React, { useEffect, useRef, useState } from 'react';
import Button from '../../../components/common/Button';
import { agentApi } from '../../../services/agentApi';
import {
  AgentEvent, AgentResultPayload, AgentToolFile, AgentToolResultPayload, AgentUserMessagePayload,
} from '../../../types/interfaces';
import DownloadButton from './DownloadButton';
import Markdown from './Markdown';
import PlanCard, { OUTCOME_UNKNOWN } from './PlanCard';
import { PlanState, ThreadItem, formatBytes, threadItems, toolStepCount } from './agentThread';

/** A rejected promise tells the plan card the action failed (its buttons come back). */
type Action = Promise<unknown> | void;

export interface ChatThreadProps {
  events: AgentEvent[];
  plans: Record<number, PlanState>;
  onApprove(plan: PlanState): Action;
  onRevise(plan: PlanState, comment: string): Action;
  onCancel(plan: PlanState): Action;
  /** Plan id with a request in flight. */
  busyPlanId?: number | null;
}

/** Keeps one malformed event from blanking the whole thread. */
class ItemBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) {
      return <div className="agent-msg agent-meta" role="status">Could not display this item</div>;
    }
    return this.props.children;
  }
}

function UserMessage({ payload }: { payload: AgentUserMessagePayload }) {
  return (
    <div className="agent-msg agent-msg--user">
      {payload.revise_plan_id ? <div className="agent-meta">Revision of plan #{payload.revise_plan_id}</div> : null}
      <div className="agent-msg__text">{payload.text}</div>
      {(payload.attachments || []).length > 0 && (
        <div className="agent-attachments">
          {payload.attachments.map((a) => <span key={a.file_id} className="agent-file-chip">{a.name}</span>)}
        </div>
      )}
    </div>
  );
}

function ToolFiles({ files }: { files: AgentToolFile[] }) {
  return (
    <>
      {files.map((f) => (
        <div key={f.file_id} className="agent-dl">
          <div><b>{f.file_name}</b><div className="agent-meta">{formatBytes(f.size)}</div></div>
          <DownloadButton fileId={f.file_id} url={f.url}>Download</DownloadButton>
        </div>
      ))}
    </>
  );
}

function argsText(args: Record<string, unknown> | undefined): string {
  if (!args) return '';
  return JSON.stringify(args, null, 2);
}

function ToolSteps({ events }: { events: AgentEvent[] }) {
  const count = toolStepCount(events);
  const files = events
    .filter((e) => e.type === 'tool_result')
    .flatMap((e) => (e.payload as AgentToolResultPayload).files || []);
  return (
    <div className="agent-msg agent-msg--tools">
      <details className="agent-details">
        <summary>
          <span className="agent-tag agent-tag--read">Looked up · {count} {count === 1 ? 'step' : 'steps'}</span>
        </summary>
        {events.map((e) => (e.type === 'tool_call' ? (
          <div key={e.seq} className="agent-tool">
            <div className="agent-meta">{String(e.payload?.name)}</div>
            <pre className="agent-code"><code>{argsText(e.payload?.args)}</code></pre>
          </div>
        ) : (
          <div key={e.seq} className="agent-tool">
            <div className={`agent-meta${e.payload?.ok === false ? ' agent-meta--error' : ''}`}>
              {String(e.payload?.name)} {e.payload?.ok === false ? 'failed' : 'result'}
            </div>
            <pre className="agent-code"><code>{String(e.payload?.content ?? '')}</code></pre>
          </div>
        )))}
      </details>
      {files.length > 0 && <ToolFiles files={files} />}
    </div>
  );
}

function ResultRow({ payload }: { payload: AgentResultPayload }) {
  const label = payload.status === 'done' ? 'Done' : payload.status === 'failed' ? 'Failed' : 'Outcome unknown';
  return (
    <div className="agent-msg agent-msg--bot">
      <div className="agent-tags">
        <span className={`agent-tag agent-tag--${payload.status}`}>{label}</span>
        <span className="agent-meta">Plan #{payload.plan_id} · audit #{payload.audit_id}</span>
      </div>
      {payload.status === 'unknown' && (
        <div className="agent-banner agent-banner--warning" role="alert">{OUTCOME_UNKNOWN}</div>
      )}
      {payload.error && <div className="agent-banner agent-banner--error">{payload.error}</div>}
      {(payload.files || []).map((f) => (
        <div key={f.file_id} className="agent-dl">
          <div><b>{f.file_name}</b>{f.size !== null && <div className="agent-meta">{formatBytes(f.size)}</div>}</div>
          <DownloadButton fileId={f.file_id} url={f.url}>Download</DownloadButton>
        </div>
      ))}
    </div>
  );
}

function NoteSuggestion({ text }: { text: string }) {
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const save = async () => {
    setState('saving');
    try {
      await agentApi.createNote(text);
      if (mounted.current) setState('saved');
    } catch {
      if (mounted.current) setState('error');
    }
  };
  return (
    <div className="agent-msg agent-note">
      <div className="agent-meta">Suggested note</div>
      <div className="agent-msg__text">{text}</div>
      <div className="agent-plan__buttons">
        {state === 'saved' ? (
          <span className="agent-meta" role="status">Saved</span>
        ) : (
          <Button variant="outline" size="sm" onClick={save} disabled={state === 'saving'}>Save note</Button>
        )}
        {state === 'error' && <span className="agent-inline-error" role="status">Could not save the note</span>}
      </div>
    </div>
  );
}

const ChatThread: React.FC<ChatThreadProps> = ({ events, plans, onApprove, onRevise, onCancel, busyPlanId }) => {
  const renderItem = (item: ThreadItem): React.ReactNode => {
    if (item.kind === 'tools') return <ToolSteps events={item.events} />;
    if (item.kind === 'plan') {
      const plan = plans[item.planId];
      if (!plan) return null;
      return (
        <PlanCard
          plan={plan}
          reapproval={plan.status === 'awaiting_approval' ? item.reapproval : null}
          busy={busyPlanId === plan.id}
          onApprove={() => onApprove(plan)}
          onRevise={(comment) => onRevise(plan, comment)}
          onCancel={() => onCancel(plan)}
        />
      );
    }
    const e = item.event;
    switch (e.type) {
      case 'user_message':
        return <UserMessage payload={e.payload} />;
      case 'assistant_message':
        return (
          <div className={`agent-msg agent-msg--bot${e.payload?.interim ? ' agent-msg--interim' : ''}`}>
            <Markdown text={String(e.payload?.text ?? '')} />
          </div>
        );
      case 'result':
        return <ResultRow payload={e.payload} />;
      case 'error':
        return (
          <div className="agent-msg agent-banner agent-banner--error" role="alert">
            {String(e.payload?.text ?? 'Something went wrong')}
          </div>
        );
      case 'note_suggestion':
        return <NoteSuggestion text={String(e.payload?.text ?? '')} />;
      default:
        return null;
    }
  };

  /** Plan cards remount when the fingerprint changes (reapproval), which re-enables Approve. */
  const keyOf = (item: ThreadItem): string => {
    if (item.kind === 'tools') return `t${item.key}`;
    if (item.kind === 'plan') return `p${item.planId}:${plans[item.planId]?.fingerprint ?? ''}`;
    return `e${item.event.seq}`;
  };

  return (
    <div className="agent-thread">
      {threadItems(events).map((item) => <ItemBoundary key={keyOf(item)}>{renderItem(item)}</ItemBoundary>)}
    </div>
  );
};

export default ChatThread;
